/**
 * @file MoneyInput.js
 * @description A reusable input component for monetary values with formatting, validation, and tooltip support.
 */

import { useState, useEffect } from 'react';
import Tooltip from '../../ui/tooltips/Tooltip';

const MoneyInput = ({
    className,
    inlineLabel = true,
    value,
    onChange,
    name,
    label,
    isRequired = false,
    errorMessage,
    hideErrorMessage = false,
    disabled = false,
    placeholder = '0.00',
    showToolTip = false,
    tooltipText = '',
    subLabel = '',
}) => {
    const [formattedValue, setFormattedValue] = useState('');
    const [cursorPosition, setCursorPosition] = useState(0);

    useEffect(() => {
        if (value) {
            setFormattedValue(formatMoney(value));
        } else {
            setFormattedValue('');
        }
    }, [value]);

    useEffect(() => {
        const input = document.getElementById(name);
        if (input && document.activeElement === input) {
            input.setSelectionRange(cursorPosition, cursorPosition);
        }
    }, [formattedValue, cursorPosition, name]);

    const formatMoney = value => {
        if (!value) return '';

        const isNegativePrefix = value[0] === '-';
        const isSingleNegativeSign = isNegativePrefix && value.length === 1;

        if (isSingleNegativeSign) return '-';

        const number = parseFloat(value);

        if (isNaN(number)) return '';

        const isNegative = number < 0;
        const absNumber = Math.abs(number);
        const formatted = absNumber
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        return isNegative ? `-${formatted}` : formatted;
    };

    const handleChange = e => {
        const input = e.target;
        const originalPosition = input.selectionStart;
        const originalValue = input.value;
        let newValue = input.value.replace(/(?!^)-/g, '').replace(/,/g, '');

        const decimalCount = (newValue.match(/\./g) || []).length;
        if (decimalCount > 1) {
            const firstDecimalIndex = newValue.indexOf('.');
            newValue =
                newValue.slice(0, firstDecimalIndex + 1) +
                newValue.slice(firstDecimalIndex + 1).replace(/\./g, '');
        }

        const formattedValue = formatMoney(newValue);
        let newPosition = originalPosition;
        const lengthDiff = formattedValue.length - originalValue.length;

        const commasBeforeOriginal = (
            originalValue.slice(0, originalPosition).match(/,/g) || []
        ).length;
        const commasBeforeNew = (
            formattedValue
                .slice(0, originalPosition + lengthDiff)
                .match(/,/g) || []
        ).length;
        newPosition += commasBeforeNew - commasBeforeOriginal;

        setFormattedValue(formattedValue);
        setCursorPosition(newPosition);
        onChange(newValue ? parseFloat(newValue) : null);
    };

    const handleClear = () => {
        setFormattedValue('');
        onChange(null);
    };

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className} ${errorMessage ? 'has-error' : ''}`}
        >
            <label htmlFor={name}>
                {label}
                {subLabel && (
                    <>
                        <br />
                        <div style={{ fontSize: '14px' }}>{subLabel}</div>
                    </>
                )}
                {isRequired && <span className="text-danger">*</span>}
                {showToolTip && (
                    <Tooltip html={tooltipText} placement="right">
                        <i
                            className="bi bi-info-circle"
                            style={{
                                paddingLeft: '5px',
                                fontSize: '13px',
                                color: 'var(--iNGAGED-Primary-Color)',
                            }}
                        ></i>
                    </Tooltip>
                )}
            </label>
            <div className="input-group">
                <input
                    id={name}
                    type="text"
                    className={`form-control ${errorMessage ? 'is-invalid' : ''}`}
                    value={formattedValue}
                    onChange={handleChange}
                    name={name}
                    disabled={disabled}
                    placeholder={placeholder}
                />
            </div>
            {errorMessage && !hideErrorMessage && (
                <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
        </div>
    );
};

export default MoneyInput;
